/**
 * TourContext
 *
 * Manages the full lifecycle of product tours.
 *
 * State model (dual-write: localStorage cache + backend API):
 *  - tourProgress  Persisted to backend + localStorage cache.
 *                  localStorage gives instant display; backend hydration
 *                  overwrites on mount so clearing the browser doesn't
 *                  reset progress.
 *
 * Key behaviours:
 *  - shouldShowTour: suppresses tours that are completed, dismissed ≥ 3 times,
 *    dismissed within the last 7 days, or set to remind_later within 24 hours.
 *  - pendingTour: allows a tour to be queued and auto-started after navigation
 *    to a different page (with a 500 ms delay).
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import type { ReactNode } from 'react';
import { getTourById } from '@/config/tours';
import { apiFetch } from '@/lib/api-error-handler';
import { getApiBaseUrl } from '@/lib/data-service';
import type {
  Tour,
  TourState,
  TourProgress,
  TourStatus,
  TourContextType,
} from '@/types/product-tour';

// ── Storage helpers ───────────────────────────────────────────────────────────

const STORAGE_KEY = 'eb-projects-tour-progress';
const PENDING_KEY = 'eb-projects-tour-pending';

function rehydrateDates(parsed: Record<string, TourState>): TourProgress {
  for (const state of Object.values(parsed)) {
    if (state.startedAt) state.startedAt = new Date(state.startedAt);
    if (state.completedAt) state.completedAt = new Date(state.completedAt);
    if (state.lastInteraction) state.lastInteraction = new Date(state.lastInteraction);
  }
  return parsed;
}

function readProgress(): TourProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, TourState>;
    return rehydrateDates(parsed);
  } catch {
    return {};
  }
}

function writeProgress(progress: TourProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Storage quota exceeded or unavailable — fail silently
  }
}

// Fire-and-forget API call — never blocks the UI
function persistToApi(progress: TourProgress): void {
  const url = `${getApiBaseUrl()}/tour-progress`;
  apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ progressJson: JSON.stringify(progress) }),
  }).catch((err) => {
    console.warn('[TourContext] Backend persist failed — localStorage is still up to date:', err);
  });
}

// ── Context ───────────────────────────────────────────────────────────────────

const TourContext = createContext<TourContextType | undefined>(undefined);

// ── Provider ─────────────────────────────────────────────────────────────────

export function TourProvider({ children }: { children: ReactNode }) {
  const [tourProgress, setTourProgress] = useState<TourProgress>(readProgress);
  const [currentTour, setCurrentTour] = useState<Tour | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);

  // Track whether backend sync is active so we only persist when auth is ready
  const backendSyncEnabled = useRef(false);
  const [isBackendSynced, setIsBackendSynced] = useState(false);

  // Persist whenever progress changes
  useEffect(() => {
    writeProgress(tourProgress);
    if (backendSyncEnabled.current) {
      persistToApi(tourProgress);
    }
  }, [tourProgress]);

  // ── Internal helpers ────────────────────────────────────────────────────────

  const updateTourProgress = useCallback(
    (tourId: string, partial: Partial<TourState>) => {
      setTourProgress(prev => {
        const existing = prev[tourId] ?? {
          tourId,
          currentStep: 0,
          totalSteps: 0,
          status: 'not_started' as TourStatus,
          dismissalCount: 0,
          skippedSteps: [],
        };
        return { ...prev, [tourId]: { ...existing, ...partial, lastInteraction: new Date() } };
      });
    },
    [],
  );

  // ── Backend sync initialiser (called by useTourBackendSync) ────────────────

  const enableBackendSync = useCallback((backendProgress: TourProgress) => {
    backendSyncEnabled.current = true;
    setIsBackendSynced(true);
    if (Object.keys(backendProgress).length > 0) {
      setTourProgress(prev => {
        // Backend wins — merge backend data over localStorage
        const merged = { ...prev };
        for (const [tourId, backendState] of Object.entries(backendProgress)) {
          merged[tourId] = backendState;
        }
        return merged;
      });
    }
  }, []);

  // ── Public API ───────────────────────────────────────────────────────────────

  const startTour = useCallback(
    (tourId: string) => {
      const tour = getTourById(tourId);
      if (!tour) {
        console.warn(`[TourContext] Tour "${tourId}" not found`);
        return;
      }
      setCurrentTour(tour);
      setCurrentStep(0);
      setIsActive(true);
      updateTourProgress(tourId, {
        tourId,
        currentStep: 0,
        totalSteps: tour.steps.length,
        status: 'in_progress',
        startedAt: new Date(),
      });
    },
    [updateTourProgress],
  );

  const completeTour = useCallback(() => {
    if (!currentTour) return;
    updateTourProgress(currentTour.id, {
      status: 'completed',
      completedAt: new Date(),
    });
    setIsActive(false);
    setCurrentTour(null);
    setCurrentStep(0);
  }, [currentTour, updateTourProgress]);

  const nextStep = useCallback(() => {
    if (!currentTour) return;
    const lastIndex = currentTour.steps.length - 1;
    if (currentStep >= lastIndex) {
      completeTour();
    } else {
      const next = currentStep + 1;
      setCurrentStep(next);
      updateTourProgress(currentTour.id, { currentStep: next });
    }
  }, [currentTour, currentStep, completeTour, updateTourProgress]);

  const previousStep = useCallback(() => {
    if (!currentTour || currentStep === 0) return;
    const prev = currentStep - 1;
    setCurrentStep(prev);
    updateTourProgress(currentTour.id, { currentStep: prev });
  }, [currentTour, currentStep, updateTourProgress]);

  const skipTour = useCallback(() => {
    if (!currentTour) return;
    const existing = tourProgress[currentTour.id];
    updateTourProgress(currentTour.id, {
      status: 'skipped',
      dismissalCount: (existing?.dismissalCount ?? 0) + 1,
    });
    setIsActive(false);
    setCurrentTour(null);
    setCurrentStep(0);
  }, [currentTour, tourProgress, updateTourProgress]);

  const dismissTour = useCallback(() => {
    if (!currentTour) return;
    const existing = tourProgress[currentTour.id];
    updateTourProgress(currentTour.id, {
      status: 'dismissed',
      dismissalCount: (existing?.dismissalCount ?? 0) + 1,
    });
    setIsActive(false);
    setCurrentTour(null);
    setCurrentStep(0);
  }, [currentTour, tourProgress, updateTourProgress]);

  const restartTour = useCallback(
    (tourId: string) => {
      setTourProgress(prev => {
        const updated = { ...prev };
        delete updated[tourId];
        return updated;
      });
      // Use a small defer so state clears before startTour reads it
      setTimeout(() => startTour(tourId), 50);
    },
    [startTour],
  );

  const getTourState = useCallback(
    (tourId: string): TourState | undefined => tourProgress[tourId],
    [tourProgress],
  );

  const shouldShowTour = useCallback(
    (tourId: string): boolean => {
      const state = tourProgress[tourId];
      if (!state) return true;

      if (state.status === 'completed') return false;

      if (state.dismissalCount >= 3) return false;

      if (state.status === 'dismissed' && state.lastInteraction) {
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - state.lastInteraction.getTime() < sevenDaysMs) return false;
      }

      if (state.status === 'remind_later' && state.lastInteraction) {
        const twentyFourHoursMs = 24 * 60 * 60 * 1000;
        if (Date.now() - state.lastInteraction.getTime() < twentyFourHoursMs) return false;
      }

      return true;
    },
    [tourProgress],
  );

  // ── Pending tour (start after navigation) ─────────────────────────────────

  const setPendingTour = useCallback((tourId: string) => {
    try { localStorage.setItem(PENDING_KEY, tourId); } catch { /* ignore */ }
  }, []);

  const getPendingTour = useCallback((): string | null => {
    try { return localStorage.getItem(PENDING_KEY); } catch { return null; }
  }, []);

  const clearPendingTour = useCallback(() => {
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
  }, []);

  const startPendingTour = useCallback(() => {
    const pendingId = getPendingTour();
    if (!pendingId) return;
    clearPendingTour();
    setTimeout(() => startTour(pendingId), 500);
  }, [getPendingTour, clearPendingTour, startTour]);

  return (
    <TourContext.Provider
      value={{
        currentTour,
        currentStep,
        isActive,
        tourProgress,
        isBackendSynced,
        startTour,
        nextStep,
        previousStep,
        skipTour,
        completeTour,
        dismissTour,
        restartTour,
        getTourState,
        shouldShowTour,
        updateTourProgress,
        setPendingTour,
        getPendingTour,
        clearPendingTour,
        startPendingTour,
        enableBackendSync,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTour(): TourContextType {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside TourProvider');
  return ctx;
}

/**
 * useTourBackendSync
 *
 * Call this hook once inside the auth boundary (after BootstrapGate).
 * It fetches tour progress from the backend and enables dual-write
 * so subsequent changes are persisted to both localStorage and the API.
 */
export function useTourBackendSync(): void {
  const ctx = useContext(TourContext);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!ctx || hasFetched.current) return;
    hasFetched.current = true;

    apiFetch<{ progressJson: string | null }>(`${getApiBaseUrl()}/tour-progress`)
      .then((data) => {
        let backendProgress: TourProgress = {};
        if (data?.progressJson) {
          try {
            const parsed = JSON.parse(data.progressJson) as Record<string, TourState>;
            backendProgress = rehydrateDates(parsed);
          } catch {
            // Ignore malformed JSON
          }
        }
        ctx.enableBackendSync(backendProgress);
      })
      .catch((err) => {
        console.warn('[TourContext] Backend fetch failed — using localStorage cache:', err);
        // Still enable backend sync so future writes go to the API
        ctx.enableBackendSync({});
      });
  }, [ctx]);
}
